import { DropDownList } from "../../../components/ui";
import { Button } from "../../../components/ui";
import DispatchStage from "../dailyDispatch/dispatchStage/DispatchStage";
import { Spinner } from "../../../components/ui";
import { WorkerList } from "../../../components/ui";
import { LockClosed, LockOpen } from "../../../components/icons/svg/outline";
import { formatDate, formatShortMonthDay } from "../../../utils/datetime/core";
import WeatherCard from "./WeatherCard";
import { formatTemperature, getWeatherIcon } from "./weatherUtils";

/**
 * Compact shared summary of one planning DayDetails row for Dispatch and Schedule.
 *
 * @param {Object} props
 * @param {Date} props.date Calendar day represented by this summary.
 * @param {Object|null} props.dayDetails Backend DayDetails row for the date.
 * @param {Object|null} [props.weather] Date-keyed weather row for the date.
 * @param {Array<Object>} [props.assignedWorkers=[]] Workers assigned to active scopes/aspects.
 * @param {Array<Object>} [props.offWorkWorkers=[]] Workers marked off-work on DayDetails.
 * @param {Array<Object>} [props.availableWorkers=[]] Workers available after assigned/off-work exclusions.
 * @param {Object|null} [props.menuItems=null] Optional ellipsis menu labels and handlers.
 * @param {Array<string>} [props.disabledMenuItems=[]] Optional disabled menu labels.
 * @param {"left"|"right"} [props.menuAlign] Optional dropdown alignment override.
 * @param {"dispatch"|"schedule"} [props.type="dispatch"] Layout type for the host page.
 * @param {boolean} [props.showDate=false] Whether to show the date in the header.
 * @param {import('react').ReactNode} [props.title] Optional header content, used by Dispatch inline cards.
 * @param {() => void} [props.onOffWorkClick] Optional off-work capsule click handler.
 * @param {boolean} [props.offWorkDisabled=false] Whether off-work editing is disabled.
 * @param {string} [props.offWorkTitle] Optional title for the off-work capsule.
 * @param {boolean} [props.dayLocked=false] Whether the DayDetails row is locked.
 * @param {() => void|Promise<void>} [props.onDayLockToggle] Optional lock toggle handler.
 * @param {boolean} [props.isFirstDay=false] Whether this is the leftmost rendered day.
 * @param {boolean} [props.isLastDay=false] Whether this is the rightmost rendered day.
 * @param {string} [props.className=""] Extra classes on the card root.
 * @param {string} [props.bodyClassName=""] Extra classes on the worker capsule grid.
 * @returns {JSX.Element}
 */
export default function DayDetails({
  date,
  dayDetails,
  weather = null,
  assignedWorkers = [],
  offWorkWorkers = [],
  availableWorkers = [],
  menuItems = null,
  disabledMenuItems = [],
  menuAlign,
  type = "dispatch",
  showDate = false,
  onOffWorkClick,
  offWorkDisabled = false,
  offWorkTitle,
  dayLocked = false,
  onDayLockToggle,
  isFirstDay = false,
  isLastDay = false,
  className = "",
  bodyClassName = "",
}) {
  const dailyWeather = weather?.data?.daily ?? null;
  const primaryCondition = dailyWeather?.weather?.[0] ?? {};
  const WeatherIcon = getWeatherIcon(primaryCondition, dailyWeather);
  const weatherIconColor =
    weather?.status === "cached" ? "text-gray-800" : "text-amber-500";
  const canEditOffWork = typeof onOffWorkClick === "function" && !offWorkDisabled;
  const offWorkCursorClass = canEditOffWork ? "cursor-pointer" : "cursor-default";
  // Future dates past the forecast window vs past dates with no stored history;
  // both are terminal (never resolve), so show a message instead of a spinner.
  const weatherBeyondForecastWindow =
    weather?.status === "out_of_range" &&
    weather?.reason === "outside_supported_weather_window";
  const weatherBeyondHistoryWindow =
    weather?.status === "unavailable" &&
    weather?.reason === "beyond_historic_storage_threshold";
  const weatherUnavailable =
    weatherBeyondForecastWindow || weatherBeyondHistoryWindow;

  if (type === "schedule") {
    return (
      <div className={`relative text-xs rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm ${className}`}>
        <div className="text-left font-semibold text-gray-800 ml-4 text-base">
          {formatDate(date, "d mon o DD, YYYY")}
        </div>
        <div className="mb-1 flex min-h-[54px] items-start justify-between gap-3 p-1">
          {dailyWeather ? (
            <div className="group/weather cursor-default relative mt-1 ml-2 flex w-10 shrink-0 flex-col items-center text-gray-700">
              <WeatherIcon className={`h-8 w-8 ${weatherIconColor} -mt-1`} aria-hidden />
              <div className="-mt-1 font-semibold">
                {formatTemperature(dailyWeather.temp?.day)}
              </div>
              <WeatherCard dailyWeather={dailyWeather} status={weather?.status} />
            </div>
          ) : weatherUnavailable ? (
            <WeatherUnavailableMessage beyondHistory={weatherBeyondHistoryWindow} />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center text-gray-400">
              <Spinner size="h-5 w-5" />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col items-end gap-2 mr-2">
            <DispatchStage
              className="font-medium"
              dayDetails={dayDetails}
              isFirstDay={isFirstDay}
              isLastDay={isLastDay}
            />
            {typeof onDayLockToggle === "function" && (
              <Button
                variant="custom"
                text={dayLocked ? "Locked" : "Unlocked"}
                icon={
                  dayLocked ? (
                    <LockClosed className="h-3 w-3" aria-hidden />
                  ) : (
                    <LockOpen className="h-3 w-3" aria-hidden />
                  )
                }
                onClick={onDayLockToggle}
                className={`flex items-center text-xs gap-1.5 w-[90px] border border-gray-400 px-2 py-0 rounded-xl font-medium transition-colors ${dayLocked
                    ? "bg-green-800 text-white hover:bg-gray-700"
                    : "bg-white text-gray-800 hover:bg-gray-100"
                  }`}
              />
            )}
          </div>
        </div>

        <div className={`grid grid-cols-3 gap-0 text-center font-normal ${bodyClassName}`}>
          <WorkerCapsule
            label="Assigned"
            workers={assignedWorkers}
            borderClass="border-r"
            alignClass="left-0 origin-top-left"
          />
          <WorkerCapsule
            label="Off-Work"
            workers={offWorkWorkers}
            alignClass="left-1/2 -translate-x-1/2 origin-top"
            className={offWorkCursorClass}
            onClick={canEditOffWork ? onOffWorkClick : undefined}
            title={offWorkTitle}
          />
          <WorkerCapsule
            label="Available"
            workers={availableWorkers}
            borderClass="border-l"
            alignClass="right-0 origin-top-right"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative text-xs xl:text-sm rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm ${className}`}>
      <div className="mb-1 grid min-h-[54px] grid-cols-3 gap-0 p-1 text-center">
        <div className="flex justify-center">
          {dailyWeather ? (
            <div className="group/weather relative mt-1 flex w-10 shrink-0 flex-col items-center text-gray-700">
              <div className="flex flex-col items-center">
                <WeatherIcon className={`h-8 w-8 ${weatherIconColor} -mt-1`} aria-hidden />
                <div className="-mt-1 font-semibold">
                  {formatTemperature(dailyWeather.temp?.day)}
                </div>
              </div>
              <WeatherCard
                dailyWeather={dailyWeather}
                status={weather?.status}
                positionClassName={
                  menuAlign === "right"
                    ? "left-1/2 -translate-x-1/2 origin-top"
                    : "left-0 origin-top-left"
                }
              />
            </div>
          ) : weatherUnavailable ? (
            <WeatherUnavailableMessage beyondHistory={weatherBeyondHistoryWindow} />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center text-gray-400">
              <Spinner size="h-5 w-5" />
            </div>
          )}
        </div>
        <div aria-hidden />
        <div className="flex min-w-0 justify-center">
          <div className="flex min-w-0 flex-col items-end gap-0">
            {showDate && (
              <div className="shrink-0 text-center font-semibold text-gray-800">
                {date.toLocaleDateString(undefined, { weekday: "short" })}{" "}
                {formatShortMonthDay(date)}
              </div>
            )}
            {menuItems && (
              <div className="flex items-center justify-end gap-1.5 mr-4">
                <DropDownList
                  listItems={menuItems}
                  className="w-4"
                  buttonIcon="ellipsis"
                  align={menuAlign}
                  disabledItems={disabledMenuItems}
                  buttonClassName="ml-0 mb-2 bg-gray-200 rounded-lg px-2 hover:bg-gray-300"
                />
              </div>
            )}
            <div className="flex min-w-0 items-center justify-end">
              <DispatchStage
                className="font-medium"
                dayDetails={dayDetails}
                isFirstDay={isFirstDay}
                isLastDay={isLastDay}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-3 gap-0 text-center font-normal ${bodyClassName}`}>
        <WorkerCapsule
          label="Assigned"
          workers={assignedWorkers}
          borderClass="border-r"
          alignClass="left-0 origin-top-left"
        />
        <WorkerCapsule
          label="Off-Work"
          workers={offWorkWorkers}
          alignClass="left-1/2 -translate-x-1/2 origin-top"
          className={offWorkCursorClass}
          onClick={canEditOffWork ? onOffWorkClick : undefined}
          title={offWorkTitle}
        />
        <WorkerCapsule
          label="Available"
          workers={availableWorkers}
          borderClass="border-l"
          alignClass="right-0 origin-top-right"
        />
      </div>
    </div>
  );
}

/**
 * Count capsule with a worker list revealed on hover.
 *
 * @param {Object} props
 * @param {string} props.label Capsule label.
 * @param {Array<Object>} props.workers Workers displayed in the hover list.
 * @param {string} props.alignClass Hover card alignment classes.
 * @param {string} [props.borderClass=""] Optional border class.
 * @param {string} [props.className=""] Extra classes.
 * @param {() => void} [props.onClick] Optional click handler.
 * @param {string} [props.title] Optional title text.
 * @returns {JSX.Element}
 */
function WorkerCapsule({
  label,
  workers,
  alignClass,
  borderClass = "",
  className = "",
  onClick,
  title,
}) {
  return (
    <div
      className={`group relative flex flex-col rounded-md pb-1 transition-colors ease-in-out hover:bg-gray-300 ${borderClass} ${className}`}
      onClick={onClick}
      title={title}
    >
      <p className="truncate px-1 py-1">{label}</p>
      <p>{workers.length}</p>
      {workers.length > 0 && (
        <div
          className={`absolute top-full z-[70] mt-1 min-w-[200px] scale-0 rounded-md bg-green-800 p-1 px-2 text-normal transition-transform ease-in-out group-hover:scale-100 ${alignClass}`}
        >
          <WorkerList
            workers={workers}
            className="relative text-left text-white"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Compact weather placeholder for dates with no available weather.
 *
 * @param {Object} props
 * @param {boolean} [props.beyondHistory=false] When true, the date is a past day
 *   with no stored history (and too old to back-fill); otherwise it is a future
 *   date beyond the supported forecast window.
 * @returns {JSX.Element}
 */
function WeatherUnavailableMessage({ beyondHistory = false }) {
  if (beyondHistory) {
    return (
      <div
        className="flex min-h-10 w-24 shrink-0 flex-col items-center justify-center text-center text-[9px] font-medium leading-tight text-gray-500"
        title="Day is beyond historic data storage threshold"
      >
        <span>Beyond historic</span>
        <span>data storage</span>
        <span className="font-normal">threshold</span>
      </div>
    );
  }
  return (
    <div
      className="flex min-h-10 text-[9px] w-20 shrink-0 flex-col items-center justify-center text-center font-medium leading-tight text-gray-500"
      title="Weather is only available within 30 days."
    >
      <span>Weather</span>
      <span>not available</span>
      <span className="font-normal">beyond 30 days</span>
    </div>
  );
}
